import { Clipboard } from '@angular/cdk/clipboard';
import { inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isEmpty, isEqual } from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';

import {
  catchError,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

import {
  Asset,
  AvailableZonesGQL,
  BaseZoneFragment,
  CalendarEvent,
  CalendarEventLocation,
  CalendarEventLocationInput,
  CreateCalendarEventGQL,
  CreateCommentGQL,
  CreateUserInputWithRole,
  DeleteCommentsGQL,
  DeleteInvoicesGQL,
  DeleteJobGQL,
  EditProfileGQL,
  ExportInvoiceToQuickbooksGQL,
  GetInvoiceLinksGQL,
  InvoiceDetailsGQL,
  Job_LocationFragment,
  Job_ZoneWithParentAndTimezoneFragment,
  JobsV2PageGQL,
  JobsV2PageQueryVariables,
  ListArtifactsGQL,
  ListBaseAssetsGQL,
  ListBaseAssetsQueryVariables,
  ListCommentsWithRepliesGQL,
  ListUsersGQL,
  ListUsersQueryVariables,
  RemoveCalendarEventGQL,
  SendDocumentGQL,
  SendInvoicesGQL,
  SetTagsOnObjectGQL,
  SetTagsOnObjectMutationVariables,
  SingleEditInput,
  TagsGQL,
  TagsQueryVariables,
  UpdateCommentsGQL,
  UpdateInvoicesGQL,
  UpdateJobGQL,
  UpdateJobInput,
  User
} from '../../generated/graphql.generated';

import { QuickAccessActions } from '../base/menu/quick-access/quick-access.actions';
import { generateMutationContext } from '../core/auth/session';
import { PlusAuthenticationService } from '../core/public-api';
import { EventLocationInfo } from '../estimates/estimates.component';
import { CREW_ROLE_ATTRIBUTE, EMPLOYEE_ROLE_ATTRIBUTE, JOB_FORM_FIELDS } from '../global.constants';
import { TAG_TYPES } from '../interfaces/tag';
import { isDraftInvoice } from '../invoices/invoices.utils';
import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaHelperService } from '../services/freya-helper.service';

import { FreyaNotificationsService } from '../services/freya-notifications.service';

import { ResponsiveHelperService } from '../services/responsive-helper.service';
import { UpdateEventLocationsDialogComponent } from '../shared/update-event-locations-dialog/update-event-locations-dialog.component';

import { BrandingActions } from '../state/branding.store';
import { currentTimeSeconds } from '../time';
import { generateUUID } from '../utilities/state.util';


import { sortCommentsWithReplies } from './job-activity/comments.utils';
import { ScheduleEventsActions } from './job-state/event-schedule-state/event-schedule.actions';
import { selectDefaultForIncludeDockTravel } from './job-state/event-schedule-state/event-schedule.selectors';
import { JobToolSubscriptionActions } from './job-state/job-tool-subscription.actions';
import { WorkOrdersActions } from './job-state/workorders-state/workorders.actions';
import { JobsV2LocationsChange, JobToolActions } from './job-tool.actions';
import {
  jobToolFeature,
  UserWithName,
} from './job-tool.reducer';
import { extractFieldsFromResponse, generateCreateCustomerInput, generateFieldsInputFromLatestChanges, generateInventoryInputFromLatestChanges, generateJobCreateVariables, generateLocationInputsForUpdateJobGQL, generateUpdateCustomerInput } from './jobsv2-helpers';
import { CloseJobDialogState, CloseJobV2Component } from './jobv2-create/close-job-componentV2/close-job-componentV2.component';
import { jobCreateSelectors, selectCustomerRole, selectLatestInvoiceId } from './jobv2-create/jobv2-create-state/jobv2-create.selectors';

const mapJobUsers = (users: UserWithName[]) =>
  users.map((user) => ({
    role: user.role,
    userId: user.id,
  }));

export const setDefaultValuesFromConfig = createEffect(() => {
  const store = inject(Store);
  const actions$ = inject(Actions);

  return actions$.pipe(
    ofType(BrandingActions.brandingUpdated),
    withLatestFrom(store.select(selectDefaultForIncludeDockTravel)),
    map(([_, includeDockTravel]) => {
      return ScheduleEventsActions.includeDockTravelChecked({ includeDockTravel });
    }),
  );
}, {functional: true, dispatch: true});

export const updateRecentJobs = createEffect(() => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  return actions$.pipe(
    ofType(
      JobToolActions.jobUpdateSuccess,
      WorkOrdersActions.changesSavedSuccess,
    ),
    withLatestFrom(store.select(jobToolFeature.selectJobToolState)),
    switchMap(([ _, jobToolState ]) => of(QuickAccessActions.jobUpdated({ ...jobToolState }))),
  );
}, { functional: true, dispatch: true });

export const addComment = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    createCommentGQL = inject(CreateCommentGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.addComment),
      withLatestFrom(
        store.select(jobToolFeature.selectJob),
        store.select(jobToolFeature.selectJobZone),
        store.select(jobToolFeature.selectCurrentCommentMode),
      ),
      exhaustMap(([action, job, zone, currentCommentMode]) => {
        const input = action.input;

        return createCommentGQL
          .mutate(
            {
              input: {
                ...input,
                objectId: job.id,
                objectType: 'Job',
                mode: currentCommentMode,
              },
            },
            {
              context: generateMutationContext({
                zone: zone?.id,
                actionId: input.id,
              }),
            },
          )
          .pipe(
            map((result) =>
              JobToolActions.addCommentSuccess({
                comment: result.data?.addComment,
                commentId: input.id,
              }),
            ),
            catchError((error) =>
              of(
                JobToolActions.addCommentError({
                  error,
                  action,
                  commentId: input.id,
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const updateComment = createEffect(
  (
    actions$ = inject(Actions),
    updateCommentGQL = inject(UpdateCommentsGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.updateComment),
      exhaustMap(({ input }) => {
        return updateCommentGQL
          .mutate({
            input,
          })
          .pipe(
            map((result) => {
              if (!result.data?.updateComments?.length) {
                return JobToolActions.updateCommentError({
                  error: new Error(`Comment not found, cannot update`),
                  input,
                });
              }

              return JobToolActions.updateCommentSuccess({
                comment: result.data?.updateComments[0],
              });
            }),
            catchError((error) =>
              of(
                JobToolActions.updateCommentError({
                  error,
                  input,
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const deleteComment = createEffect(
  (
    actions$ = inject(Actions),
    deleteCommentGQL = inject(DeleteCommentsGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.deleteComment),
      exhaustMap(({ comment }) => {
        return deleteCommentGQL
          .mutate({
            input: {
              ids: [comment.id],
            },
          })
          .pipe(
            map((result) => {
              if (!result.data?.deleteComments?.commentsDeleted?.length) {
                return JobToolActions.deleteCommentError({
                  comment,
                  error: new Error(`Comment not found, cannot delete`),
                });
              }

              return JobToolActions.deleteCommentSuccess({
                comment,
              });
            }),
            catchError((error) =>
              of(
                JobToolActions.deleteCommentError({
                  error,
                  comment,
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

/**
 * Called when we add a remote comment to load more info like
 * the author name
 */
export const loadComment = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    listCommentWithRepliesGQL = inject(ListCommentsWithRepliesGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolSubscriptionActions.remoteCommentAdded),

      // we delay so that we can wait for the add comment query
      // to finish... I dont like this
      delay(300),
      withLatestFrom(store.select(jobToolFeature.selectComments)),
      exhaustMap(([{ input, output }, comments]) => {
        // does the comment already exist?
        // const { comment } = findComment(comments, output.id);
        // if (comment) {
        // 	return of(undefined);
        // }

        return listCommentWithRepliesGQL
          .fetch({
            filter: {
              ids: [output.id],
              objectType: 'Job',
              parentsOnly: false,
              // objectIds: [],
            },
          })
          .pipe(
            map((result) => {
              const [comment] = result.data?.comments?.comments || [];
              if (!comment) {
                // some very weird remote error happened
                return undefined;
              }

              console.log(`Comment loaded after remote retrieval`);

              return JobToolActions.commentLoaded({
                comment,
              });
            }),
            // TODO: We need a better remote error
            catchError((error) =>
              of(
                JobToolActions.commentLoadError({
                  error,
                  commentId: output.id,
                }),
              ),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const tabChanged = createEffect(
  (actions$ = inject(Actions)) => {
    const router = inject(Router);
    const route = inject(ActivatedRoute);
    const store = inject(Store);
    return actions$.pipe(
      ofType(JobToolActions.tabChanged),
      withLatestFrom(store.select(jobToolFeature.selectJobId)),
      tap(([{ name }, jobId]) => {
        router.navigate(['/jobs/', jobId, name], {
          relativeTo: route,
          queryParams: route.snapshot.queryParams,
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const contextZoneEffect = createEffect((
      store = inject(Store),
      plusAuthService = inject(PlusAuthenticationService),
    ) => {

    return store.select(jobToolFeature.selectJobZone).pipe(
        filter((jobZone) => Boolean(jobZone?.id)),
        tap<Job_ZoneWithParentAndTimezoneFragment['zone']>((jobZone) => {
            const zoneForContextId =
                jobZone.type === 'area' && jobZone.parent
                ? jobZone.parent.id
                : jobZone.id;

            if (zoneForContextId !== plusAuthService.contextedZoneId) {
                plusAuthService.setContext(zoneForContextId);
            }
        }),
    )},
    { functional: true, dispatch: false },
  );

export const reloadJob = createEffect(
  (
    actions$ = inject(Actions),
    jobsV2GQL = inject(JobsV2PageGQL),
  ) => {
    return actions$.pipe(
      ofType(
        JobToolActions.paramsSet,
        // Need to refetch to get artifact signedUrl
        JobToolActions.generateJobDocumentSuccess,
        // Need to refetch to get invoice signedUrl
        JobToolActions.invoiceReadyToView,
        // Need to refetch so newly linked invoice shows on existing transactions and invoice shows as paid potentially
        JobToolActions.paymentAppliedToInvoice,
        // Need to refetch because job totals may have changed
        JobToolActions.createTransactionSuccess,
        JobToolActions.deleteTransactionSuccess,
        // Backend will throw if receipt fails to generate, even if transaction was created successfully, so we need to refetch
        JobToolActions.createTransactionError,
        JobToolActions.jobRelatedObjectMutated,
        // Need to refetch because job totals may have changed
        JobToolActions.createDamageSuccess,
        JobToolActions.updateDamageSuccess,
        JobToolActions.deleteDamageSuccess,
      ),
      exhaustMap(({ jobId }) => {

        const variables: JobsV2PageQueryVariables = {
          jobId,
          resolve: ['discounts', 'users', 'locations', 'distances', 'artifacts'],
        };

        return jobsV2GQL
          .fetch(variables, {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
          })
          .pipe(
            mergeMap((res) => {

              if (res.loading || !res?.data) {
                  return of(JobToolActions.jobLoading());
                }

                const { jobs } = res.data;
                if (!jobs.jobs?.length) {
                  return of(JobToolActions.jobNotFoundError({
                    jobId,
                  }));
                }

                const job = jobs.jobs[0];

                return of(JobToolActions.jobLoaded({
                  job,
                  comments: sortCommentsWithReplies(job.comments.comments, false),
                  fields: extractFieldsFromResponse(job.fields),
                  totalComments: job.comments.total,
                  documents: job.artifacts,
                  invoices: job.invoices,
                  transactions: job.transactions,
                }));

              }),
              catchError((error) => {
                // TODO: fire job load error
                return of(JobToolActions.jobLoadError({
                  jobId, error
                }));
              }),
            );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const updateJobDependencies = createEffect(
  (
    actions$ = inject(Actions),
    detailsHelper = inject(DetailsHelperService),
  ) => {
    return actions$.pipe(
      ofType(JobToolSubscriptionActions.remoteJobUpdated),
      tap(({job}) => {
        // 1. Push data to details helper
        if(detailsHelper.rightPanelOpen && detailsHelper.detailsItem.value?.item?.id === job.id) {
          detailsHelper.pushUpdate({
            id: job?.id,
            type: 'Jobs',
            action: 'update',
          });
        }
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const sendEstimate = createEffect((
  actions$ = inject(Actions),
  sendDocumentGQL = inject(SendDocumentGQL),
  notify = inject(FreyaNotificationsService),
) => {

  return actions$.pipe(
    ofType(JobToolActions.sendEstimateButtonClicked),
    switchMap(({ estimate }) => sendDocumentGQL.mutate({
      artifactId: estimate.id,
      recipientRole: 'customer',
    }).pipe(
      map(() => {
        notify.success('Estimate sent');
        return JobToolActions.sendEstimateSuccess({ estimate });
      }),
      catchError((error) => {
        notify.apolloError('Failed to send estimate', error);
        return of(JobToolActions.sendEstimateError({ error, estimate }));
      }),
    ),
  ));

  },
{ functional: true, dispatch: true },
);

export const sendInvoice = createEffect((
  actions$ = inject(Actions),
  sendInvoicesGQL = inject(SendInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {

  return actions$.pipe(
    ofType(JobToolActions.sendInvoiceButtonClicked),
    switchMap(({ invoice }) => sendInvoicesGQL.mutate({
      invoiceIds: [ invoice.id ],
    }).pipe(
      map(({ data: { sendInvoices: { updated } } }) => {
        notify.success('Invoice sent');
        return JobToolActions.sendInvoiceSuccess({ invoice, updated });
      }),
      catchError((error) => {
        notify.apolloError('Failed to send invoice', error);
        return of(JobToolActions.sendInvoiceError({ error, invoice }));
      }),
    ),
  ));

  },
{ functional: true, dispatch: true },
);

export const openInvoiceAs = createEffect((
  actions$ = inject(Actions),
  getInvoiceLinksGQL = inject(GetInvoiceLinksGQL),
  freyaHelper = inject(FreyaHelperService),
  notify = inject(FreyaNotificationsService),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(JobToolActions.openInvoiceAsButtonClicked),
    switchMap(({ invoice, role }) => getInvoiceLinksGQL.mutate({
      invoiceIds: [ invoice.id ],
      role,
    }).pipe(
      withLatestFrom(store.select(jobToolFeature.selectInvoices)),
      map(([ res, invoices ]) => {

        const [ link ] = res.data.getLinks;;

        if (!link) {
          notify.error('Unable to open invoice');
          return JobToolActions.openInvoiceAsError({ error: new Error('Unable to open invoice'), invoice, role });
        }

        const currentInvoice = invoices.find((i) => i.id === invoice.id);

        if (isDraftInvoice(currentInvoice)) {
          notify.success('Invoice finalized');
        }

        freyaHelper.openInDialog(invoice?.artifacts?.[0], link);

        return JobToolActions.openInvoiceAsSuccess({ invoice, role });
      }),
      catchError((error) => {
        notify.apolloError('Failed to open invoice', error);
        return of(JobToolActions.openInvoiceAsError({ error, invoice, role }));
      }),
    )),
  );
},
{ functional: true, dispatch: true },
);

export const finalizeInvoice = createEffect((
  actions$ = inject(Actions),
  getInvoiceLinksGQL = inject(GetInvoiceLinksGQL),
  notify = inject(FreyaNotificationsService),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobToolActions.finalizeInvoiceButtonClicked),
      withLatestFrom(store.select(jobToolFeature.selectInvoices)),
      filter(([ payload, invoices ]) => {

        const currentInvoice = invoices.find((i) => i.id === payload.invoice.id);

        if (!currentInvoice) {
          return false;
        }

        return isDraftInvoice(currentInvoice);
      }),
      map(( [ payload ]) => payload.invoice),
      switchMap((invoice) => getInvoiceLinksGQL.mutate({
        invoiceIds: [ invoice.id ],
        role: 'employee',
      }).pipe(
        map((res) => {

          const [ link ] = res.data.getLinks;

          if (!link) {
            notify.error('Unable to finalize invoice');
            return JobToolActions.finalizeInvoiceError({ error: new Error('Unable to open invoice'), invoice });
          }

          notify.success('Invoice finalized');
          return JobToolActions.finalizeInvoiceSuccess({ invoice });
        }),
        catchError((error) => {
          notify.apolloError('Failed to open invoice', error);
          return of(JobToolActions.finalizeInvoiceError({ error, invoice }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

export const forceUpdateInvoice = createEffect((
  actions$ = inject(Actions),
  updateInvoicesGQL = inject(UpdateInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.forcedUpdateInvoiceButtonClicked),
      switchMap(({ invoice }) => updateInvoicesGQL.mutate({
        invoices: {
          invoiceId: invoice.id,
          updatePricingTables: true,
        }
      }).pipe(
      map(() => {
        notify.success('Invoice updated');
        return JobToolActions.forcedUpdateInvoiceSuccess({ invoice });
      }),
      catchError((error) => {
        notify.apolloError('Failed to update invoice', error);
        return of(JobToolActions.forcedUpdateInvoiceError({ error, invoice }));
      }),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const voidInvoice = createEffect((
  actions$ = inject(Actions),
  updateInvoicesGQL = inject(UpdateInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.voidInvoiceButtonPressed),
      switchMap(({ invoice }) => updateInvoicesGQL.mutate({
        invoices: {
          invoiceId: invoice.id,
          voided: true,
        }
      }).pipe(
        map(() => {
          notify.success('Invoice voided');
          return JobToolActions.voidInvoiceSuccess({ invoice });
        }),
        catchError((error) => {
          notify.apolloError('Failed to void invoice', error);
          return of(JobToolActions.voidInvoiceError({ error, invoice }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

// Delete invoice
export const deleteInvoice = createEffect((
  actions$ = inject(Actions),
  deleteInvoiceGQL = inject(DeleteInvoicesGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.deleteInvoiceButtonPressed),
      switchMap(({ invoice }) => deleteInvoiceGQL.mutate({
        invoiceIds: [ invoice.id ],
      }).pipe(
        map(() => {
          notify.success('Invoice deleted');
          return JobToolActions.deleteInvoiceSuccess({ invoice });
        }),
        catchError((error) => {
          notify.apolloError('Failed to delete invoice', error);
          return of(JobToolActions.deleteInvoiceError({ error, invoice }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

// Export invoice
export const exportInvoiceToQuickbooks = createEffect((
  actions$ = inject(Actions),
  exportInvoiceToQuickbooksGQL = inject(ExportInvoiceToQuickbooksGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.exportInvoiceToQuickbooksButtonPressed),
      switchMap(({ invoice }) => exportInvoiceToQuickbooksGQL.mutate({
        invoiceId: invoice.id,
      }).pipe(
        map((res) => {
          notify.success('Invoice exported to Quickbooks');
          return JobToolActions.exportInvoiceToQuickbooksSuccess({
            invoice,
            quickbooksId: res.data.exportInvoiceToQuickbooks,
          });
        }),
        catchError((error) => {
          notify.apolloError('Failed to export invoice to Quickbooks', error);
          return of(JobToolActions.exportInvoiceToQuickbooksError({ error, invoice }));
        }),
      )),
    );
},
{ functional: true, dispatch: true },
);

export const viewTransactionReceipt = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  freyaHelper = inject(FreyaHelperService),
  listArtifactsGQL = inject(ListArtifactsGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.viewReceiptButtonClicked),
      withLatestFrom(store.select(jobToolFeature.selectDocuments)),
      map(([ { artifactReceiptId }, artifacts ]) => {

        // Look for receipt in store
        const artifact = artifacts.find((doc) => doc.id === artifactReceiptId);

        // If we can find it, we are done
        if (artifact) {
          freyaHelper.openInDialog(artifact);
          return;
        }

        return artifactReceiptId;
      }),
      // Rest of the effect only fires if we didn't find the receipt in the store
      filter((missingArtifactId) => Boolean(missingArtifactId)),
      switchMap((missingArtifactId) => listArtifactsGQL.fetch({ filter: { ids: [ missingArtifactId ] }}).pipe(
        tap((res) => {

          const [ artifact ] = res.data.artifacts.artifacts;

          if (!artifact) {
            notify.error('Receipt not found');
            return;
          }

          freyaHelper.openInDialog(artifact);
        }),
      ))
    );
  },
{ functional: true, dispatch: false },
);

export const handleJobNotFound = createEffect(
  (
    actions$ = inject(Actions),
    availableZonesGQL = inject(AvailableZonesGQL),
    plusAuthService = inject(PlusAuthenticationService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.jobNotFoundError),
      mergeMap(({ jobId }) => {

        return availableZonesGQL
          .fetch({
            objectId: jobId,
            objectLabel: 'Job',
            limit: 1,
          })
          .pipe(
            withLatestFrom(
                store.select(jobToolFeature.selectUser),
                store.select(jobToolFeature.selectTab),
            ),
            switchMap(([ res, user, tab ]) => {
              if (!res.data.availableZones?.length) {
                return of(JobToolActions.jobLoadError({
                  jobId,
                  error: new Error(`Cannot find job in any zone with id ${ jobId }`)
                }));
              }

              // Call async function here and process `res.availableZones[0].id`
              let zone: BaseZoneFragment = res.data.availableZones[0];
              if (zone.type === 'area' && res.data.availableZones[0].parent) {
                zone = res.data.availableZones[0].parent;
              }

              if (plusAuthService.contextedZoneId === zone.id) {
                return of(JobToolActions.jobLoadError({
                  jobId,
                  error: new Error(
                    `Report this bug: Cannot find job anywhere in currently contexted zone `
                    + `even though it's supposed to be here ${ jobId }`
                  )
                }));
              }

              const newZoneId = zone.id;
              console.log(`Switching to zone for job: ${ newZoneId }`);

              return from(plusAuthService.setContext(newZoneId)).pipe(
                switchMap((res) => {
                  return of(JobToolActions.paramsSet({
                    jobId,
                    tab,
                    user,
                    skipQuickAccess: true,
                  }));
                })
              )
            }),
            catchError((error) => {
              return of(JobToolActions.jobLoadError({ jobId, error }));
            }),
          );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const handleUserMutated = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {

      const customer$ = store.select(jobToolFeature.selectCustomer);

      const job$ = store.select(jobToolFeature.selectJob);

      return actions$.pipe(
        ofType(JobToolActions.userUpdated),
        withLatestFrom(customer$, job$),
        exhaustMap(([ { userId }, customer, job ]) => {
          return userId === customer.id ? of(JobToolActions.jobRelatedObjectMutated({ jobId: job.id })) : of(JobToolActions.noOp());
        }),
      )
    },
  { functional: true, dispatch: true },
);

export const handleEventMutated = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {

      const events$ = store.select(jobToolFeature.selectJobEvents);

      const job$ = store.select(jobToolFeature.selectJob);

      return actions$.pipe(
        ofType(JobToolActions.eventUpdated),
        withLatestFrom(events$, job$),
        exhaustMap(([ { eventId }, events, job ]) => {
          const eventIds = events.map((event) => event.event.id);
          return eventIds.includes(eventId) ? of(JobToolActions.jobRelatedObjectMutated({ jobId: job.id })) : of(JobToolActions.noOp());
        }),
      )
    },
  { functional: true, dispatch: true },
);

export const handleJobError = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    store = inject(Store),
    notifications = inject(FreyaNotificationsService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.jobLoadError),
      tap(({ error }) => {

        router.navigate([ '/jobs' ]);

        notifications.error(`Failed to load job`, error.message);

        return {};

      }),
    );
  },
  { functional: true, dispatch: false },
);

export const GetUsers = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    listUsersGQL = inject(ListUsersGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.employeeSearched),
      withLatestFrom(
        store.select(jobToolFeature.selectEmployeeSearch),
      ),
      exhaustMap(([_, search]) => {
        store.dispatch(JobToolActions.employeesLoading());

        const variables: ListUsersQueryVariables = {
          filter: {
            search,
            roleHasAttribute: EMPLOYEE_ROLE_ATTRIBUTE,
          },
          limit: 5,
        };

        return listUsersGQL.fetch(variables).pipe(
          map((res) => {
            if (res.loading || !res?.data) {
              return JobToolActions.employeesLoading();
            }

            const { usersv2 } = res.data;

            return JobToolActions.employeesLoaded({
              employees: usersv2.users as User[],
            });
          }),

          catchError((error) => {
            return of(JobToolActions.employeesLoadError({ error }));
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const changeJobStage = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    updateJobGQL = inject(UpdateJobGQL),
    notify = inject(FreyaNotificationsService),
  ) => {
      return actions$.pipe(
        ofType(JobToolActions.updateJobStage),
        withLatestFrom(
          store.select(jobToolFeature.selectJob),
        ),
        exhaustMap(([ { stage }, job ]) => {
          const currentStage = job.stage;
          return updateJobGQL.mutate({ updateJobs: [{
            jobId: job.id,
            stage,
            promoteReason: 'Manually Promoted',
          }] }, {
            context: generateMutationContext({}),
          }).pipe(
            map((result) => {
              const {isSuccess, jobs: [updatedJob]} = result.data?.updateJobs;

              if (isSuccess) {
                // notify.success(`Job promoted to ${ stage }`, job.code);
                return JobToolActions.updateJobStageSuccess({
                  isSuccess,
                  updatedJob
                });
              } else {
                const error = new Error('Error updating job');
                notify.error(`Error setting job stage to ${ job.stage }`, 'Unknown Error');
                console.error('Error updating job:', error);
                return JobToolActions.updateJobStageError({
                  error,
                  stage,
                  currentStage,
                });
              }
            }),
            catchError((error) => {
              notify.error(`Error setting job stage to ${ job.stage }`, error.message);
              console.error('Error updating job:', error);
              return of(
                JobToolActions.updateJobStageError({
                  error,
                  stage,
                  currentStage,
                }),
              );
            }),

          );
        }),
      );
  },
  { functional: true, dispatch: true },
);

export const changeAgent = createEffect(
  (
    actions$ = inject(Actions),
    updateJobGQL = inject(UpdateJobGQL),
    notify = inject(FreyaNotificationsService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.jobAgentChanged),
      exhaustMap(({ updateJobInput }) => {
        const input = {
          jobId: updateJobInput.jobId,
          addUsers: mapJobUsers(updateJobInput.addUsers),
          removeUsers: mapJobUsers(updateJobInput.removeUsers),
        };

        return updateJobGQL.mutate({ updateJobs: [input] }, {context: generateMutationContext()}).pipe(
          map((result) => {
            const {isSuccess, jobs: [updatedJob]} = result.data?.updateJobs;

            if (isSuccess) {
              notify.success('Job updated successfully');
              return JobToolActions.jobAgentChangeSuccess({
                isSuccess,
                updatedJob
              });
            } else {
              const error = new Error('Error updating job');
              notify.error('Error updating job');
              console.error('Error updating job:', error);
              return JobToolActions.jobAgentChangeError({
                error,
                updateJobInput,
              });
            }
          }),
          catchError((error) => {
            notify.apolloError('Error updating job', error);
            console.error('Error updating job:', error);
            return of(
              JobToolActions.jobAgentChangeError({
                error,
                updateJobInput,
              }),
            );
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

//TODO: Optimise this effect
export const updateCustomerEffect = createEffect(() => {
  const actions$ = inject(Actions);
  const store = inject(Store);
  const editProfileGQL = inject(EditProfileGQL);
  const notify = inject(FreyaNotificationsService);

  return actions$.pipe(
    ofType(JobToolActions.jobUpdateRequested),
    withLatestFrom(
      store.select(jobToolFeature.selectChanges),
      store.select(jobCreateSelectors.selectCustomerUnderEditing),
    ),
    //if previous customer was deselected, creating new or adding another
    //existing to the job handled in updateJob effect
    filter(([_, changes]) =>
      !changes?.some(change => change.fieldName === 'existingCustomerDeselected')
    ),
    map(([_, changes, customerUnderEditing]) => {
      return generateUpdateCustomerInput(changes, customerUnderEditing as Partial<User>);
    }),
    filter(customerChanges => Boolean(customerChanges)),
    exhaustMap((customerChanges) => {
      return from(editProfileGQL.mutate(customerChanges)).pipe(
        map(response => {

          const isSuccess = response.data?.users.setProfile;
          if (isSuccess) {
            notify.success('Customer updated successfully');
            return JobToolActions.updateCustomerSuccess({ customerEditInput: customerChanges });
          } else {
            notify.error('Error updating customer');
            console.error('Error updating customer:', response);
            return JobToolActions.updateCustomerError({ error: new Error('Error updating customer') });
          }
        }),
        catchError(error => {
          notify.apolloError('Error updating customer', error);
          console.error('Error updating customer:', error);
          return of(JobToolActions.updateCustomerError({ error }));
        })
      );
    })
  );
}, { functional: true, dispatch: true });

//TODO: Optimise this effect after
//some logic here is legacy from the time when we didn't return full job from updateJob query
export const updateJob = createEffect(() => {
  const actions$ = inject(Actions);
  const store = inject(Store);
  const updateJobGQL = inject(UpdateJobGQL);
  const notify = inject(FreyaNotificationsService);

  return actions$.pipe(
    ofType(JobToolActions.jobUpdateRequested),
    withLatestFrom(
      store.select(jobToolFeature.selectChanges),
      store.select(jobToolFeature.selectJob),
      store.select(selectCustomerRole),
      store.select(jobToolFeature.selectJobTemplateVariablesData)
    ),
    exhaustMap(([{ deactivateSummaryInPlaceEditing }, changes = [], serverCopy, customerRoleId, jobTemplateVariablesData]) => {

      // Check if the job has been updated

      const jobChanges = changes.filter(item => item.namespace !== 'customerInput'
        || (item.namespace === 'customerInput' && item.fieldName === 'selectedExistingCustomer')
        || (item.namespace === 'customerInput' && item.fieldName === 'existingCustomerDeselected'));

      const customerChanges =
          changes.some(item => item.namespace === 'customerInput') &&
          !changes.some(item => item.fieldName === 'existingCustomerDeselected');

      if (!jobChanges.length) {
        // Customer changes are handled by updateCustomerEffect
        if (!customerChanges) {
          notify.success('Job is already up to date');
        }
        return of(JobToolActions.jobUpdateSuccess({ updateJobInput: { jobId: serverCopy.id } }));
      }

      const mutationId = generateUUID();

      store.dispatch(JobToolActions.jobUpdateInitiated({ mutationId }));

      const { jobType, jobOrigin, resolvedServiceArea, ...recentChanges } = generateJobCreateVariables(changes, jobTemplateVariablesData);
      const { createLocationsInput, removeLocationsInput, forRemove } = generateLocationInputsForUpdateJobGQL(changes, serverCopy);

      const fieldsChanges = generateFieldsInputFromLatestChanges(changes);
      const { inventory } = generateInventoryInputFromLatestChanges(changes);
      const selectedExistingCustomer = changes.find(
        item => item.fieldName === JOB_FORM_FIELDS.selectedExistingCustomer);

      const existingCustomerDeselected = changes.find(
          item => item.fieldName === 'existingCustomerDeselected');
      const editJobVariables: UpdateJobInput = {
        jobId: undefined, //is added below,
        metadata: {
          jobType,
          jobOrigin,
        },
        fields: {},
        addLocations: createLocationsInput,
        removeLocations: removeLocationsInput,
        ...recentChanges,
      }

      let fieldsForQuery = [];
      if (resolvedServiceArea) {
        editJobVariables.setZone = resolvedServiceArea?.id;
      }

      if (!isEmpty(fieldsChanges)) {
        fieldsForQuery = Object.keys(fieldsChanges).map(key => ({
          fieldName: key,
          value: fieldsChanges[key]
        }));
      }

      if (!isEmpty(inventory)) {
        const inventoryForQuery = {
          fieldName: "jobs.inventory",
          value: JSON.stringify(inventory)
        }

        fieldsForQuery.push(inventoryForQuery);
      }

      if (fieldsForQuery?.length) {
        editJobVariables.fields = {
          objectLabel: "Job",
          fields: fieldsForQuery,
        };
      }

      if (selectedExistingCustomer) {
        const previousCustomerId = serverCopy?.users?.find(u => u.role === 'customer')?.user?.id;
        const newCustomerId = selectedExistingCustomer?.value?.id;
        editJobVariables.removeUsers = [
          {
            role: 'customer',
            userId: previousCustomerId,
          }
        ];
        editJobVariables.addUsers = [
          {
            role: 'customer',
            userId: newCustomerId,
          }
        ]
      }

      if (existingCustomerDeselected && !selectedExistingCustomer) {
        const previousCustomerId = serverCopy?.users?.find(u => u.role === 'customer')?.user?.id;
        const createCustomerVariables = generateCreateCustomerInput(changes);
        editJobVariables.removeUsers = [
          {
            role: 'customer',
            userId: previousCustomerId,
          }
        ];
        editJobVariables.newCustomers = [{...createCustomerVariables, role: customerRoleId} as CreateUserInputWithRole]
      }

      const updateJobInput: UpdateJobInput = { ...editJobVariables, jobId: serverCopy.id };

      return updateJobGQL.mutate({ updateJobs: [updateJobInput] },  {context: generateMutationContext()}).pipe(
        map((result) => {
          const {isSuccess, jobs: [updatedJob]} = result.data?.updateJobs;

          if (isSuccess) {
            notify.success('Job updated successfully');

            return JobToolActions.jobUpdateSuccess({
              updateJobInput,
              deactivateSummaryInPlaceEditing,
              mutationId,
              updatedJob,
            });
          } else {
            notify.error('Error updating job');
            return JobToolActions.jobUpdateError({
              error: new Error('Error updating job'),
              mutationId,
            });
          }
        }),
        catchError((error) => {
          notify.apolloError('Error updating job', error);
          console.error('Error updating job:', error);
          return of(JobToolActions.jobUpdateError({ error }));
        }),
      );
    }),
  );
}, { functional: true, dispatch: true });


export const GetTags = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tagsGQL = inject(TagsGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.tagSearched),
      withLatestFrom(store.select(jobToolFeature.selectTagSearch)),
      exhaustMap(([_, search]) => {
        const variables: TagsQueryVariables = {
          filter: {
            search,
            limit: 5,
            objectTypes: [TAG_TYPES.Job],
          },
          order: 'order:asc',
        };

        return tagsGQL.fetch(variables).pipe(
          map((res) => {
            if (res.loading || !res?.data) {
              return JobToolActions.tagsLoading();
            }

            const { tags } = res.data.tags;

            return JobToolActions.tagsLoaded({
              // we must shallow clone this so that
              // the job-tags component exits its
              // loading state. It waits for a new object
              // to consider itself loaded.
              tags: [ ...tags ],
            });
          }),

          catchError((error) => {
            return of(JobToolActions.tagsLoadError({ error }));
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

// Effect to add a tag on an object (Job)
export const AssignTagsToJob = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    setTagsOnObject = inject(SetTagsOnObjectGQL),
    notify = inject(FreyaNotificationsService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.saveTagChangesClicked),
      withLatestFrom(store.select(jobToolFeature.selectJobId)),
      filter(([{ selectedTags, originalTags }, jobId]) => {
        return !isEqual(selectedTags, originalTags);
      }),
      exhaustMap(([{ selectedTags, originalTags }, jobId]) => {
        const input: SetTagsOnObjectMutationVariables = {
          tags: selectedTags.map((tag) => ({ tagId: tag.id, private: false })),
          objectId: jobId,
          objectLabel: TAG_TYPES.Job,
        };

        return setTagsOnObject.mutate(input).pipe(
          map((result) => {
            const isSuccess = result.data?.tags.setObjectTags;

            if (isSuccess) {
              notify.success('Tags assigned successfully');
              return JobToolActions.tagAssignmentSuccess({
                isSuccess,
              });

            } else {

              const error = new Error('Error assigning tags to job');

              notify.error('Error assigning tags to job');
              console.error('Error assigning tags to job:', error);

              return JobToolActions.tagAssignmentError({
                error,
                tags: selectedTags,
                originalTags
              });
            }
          }),
          catchError((error) => {

            notify.apolloError('Error assigning tags to job', error);
            console.error('Error assigning tags to job:', error);

            return of(
              JobToolActions.tagAssignmentError({
                error,
                tags: selectedTags,
                originalTags
              }),
            );
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const GetAssets = createEffect(
  (actions$ = inject(Actions), listAssets = inject(ListBaseAssetsGQL)) => {
    return actions$.pipe(
      ofType(JobToolActions.assetSearched),
      exhaustMap(({ assetSearch }) => {
        const variables: ListBaseAssetsQueryVariables = {
          filter: {
            // types: ['Truck'],
            search: assetSearch,
          },
          limit: 5,
        };



        return listAssets.fetch(variables).pipe(
          map((res) => {
            if (res.loading || !res?.data) {
              return JobToolActions.assetsSearchLoading();
            }

            const { assets } = res.data;

            return JobToolActions.assetsSearchLoaded({
              assets: assets.assets as Asset[],
            });
          }),

          catchError((error) => {
            return of(JobToolActions.assetsSearchError({ error }));
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const GetCrew = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    listUsersGQL = inject(ListUsersGQL),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.crewSearched),
      withLatestFrom(
        store.select(jobToolFeature.selectCrewSearch),
      ),
      exhaustMap(([_, search]) => {

        store.dispatch(JobToolActions.searchedCrewLoading());

        const variables: ListUsersQueryVariables = {
          filter: {
            search,
            roleHasAttribute: CREW_ROLE_ATTRIBUTE,
          },
          limit: 5,
        };

        return listUsersGQL.fetch(variables).pipe(
          map((res) => {
            if (res.loading || !res?.data) {
              return JobToolActions.searchedCrewLoading();
            }

            const { usersv2 } = res.data;

            return JobToolActions.searchedCrewLoaded({
              crew: usersv2.users as User[],
            });
          }),

          catchError((error) => {
            return of(JobToolActions.searchedCrewError({ error }));
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const CreateEvent = createEffect(
  (
    actions$ = inject(Actions),
    createCalendarEventGQL = inject(CreateCalendarEventGQL),
    notify = inject(FreyaNotificationsService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.eventCreationRequested),
      exhaustMap(({ createEventsInput, source }) => {

        return createCalendarEventGQL.mutate(createEventsInput).pipe(
          map((result) => {
            const createdEvents = result.data?.createCalendarEvent.events;
            const isSuccess = createdEvents.length > 0;

            if (isSuccess) {
              notify.success('Event created successfully.');
              return JobToolActions.eventCreationSuccess({
                isSuccess,
                events: createdEvents as CalendarEvent[],
                source
              });
            } else {
              const error = new Error('Error creating an event.');
              notify.error('Error creating an event.');
              console.error('Error creating an event:', error);

              return JobToolActions.eventCreationError({
                error,
              });
            }
          }),
          catchError((error) => {
            notify.apolloError('Error creating an event.', error);
            console.error('Error creating an event:', error);
            return of(
              JobToolActions.eventCreationError({
                error,
              }),
            );
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

// TODO: use different event for this
// export const triggerBookEvent = createEffect((
//   actions$ = inject(Actions),
//   eventService = inject(EventHelperService),
// ) => {
//   return actions$.pipe(
//     ofType(JobToolActions.eventCreationSuccess),
//     tap(({ events }) => {
//       if (events.length > 0) {
//         eventService.emitBookEvent(events[0]);
//       }
//     }),
//   );
// }, { functional: true, dispatch: false });

export const DeleteEvent = createEffect(
  (
    actions$ = inject(Actions),
    removeCalendarEventGQL = inject(RemoveCalendarEventGQL),
    notify = inject(FreyaNotificationsService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.eventDeletionRequested),
      exhaustMap(({ eventId }) => {
        return removeCalendarEventGQL
          .mutate({
            ids: [eventId],
          })
          .pipe(
            map((result) => {
              const isSuccess = result.data?.removeCalendarEvent;

              if (isSuccess) {

                notify.success('Event deleted successfully');
                return JobToolActions.eventDeletionSuccess({
                  eventId,
                });

              } else {

                const error = new Error('Error deleting event');
                notify.error('Error deleting event');
                console.error('Error deleting event:', error);
                return JobToolActions.eventDeletionError({
                  error,
                });

              }
            }),
            catchError((error) => {
              notify.apolloError('Error deleting job', error);
              console.error('Error deleting job:', error);
              return of(
                JobToolActions.eventDeletionError({
                  error,
                }),
              );
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const ConfirmEventLocationChanges = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    notify = inject(FreyaNotificationsService),
    freyaHelper = inject(FreyaHelperService),
    dialogService = inject(DialogService),
    responsiveHelper = inject(ResponsiveHelperService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.jobUpdateSuccess),
      withLatestFrom(store.select(jobToolFeature.selectJob)),
      filter(([_, job]) => Boolean(job?.events?.length && job?.locations?.length)),
      exhaustMap(([_, job]) => {

        // Map job locations by type for quick lookup
        const jobLocationMap = job.locations.reduce((acc, location) => {
          if (location.locationType) {
            acc[location.locationType] = location;
          }
          return acc;
        }, {} as Record<string, Job_LocationFragment>);

        const edits: SingleEditInput[] = [];
        const confirmDialogInfo: EventLocationInfo[] = [];

        job.events.forEach(event => {
          const addLocations: CalendarEventLocationInput[] = [];
          const removeLocations: string[] = [];
          const dialogInfo: EventLocationInfo = { event, locations: [] };

          event.locations.forEach(location => {
            const jobLocation = jobLocationMap[location.type];
            if(jobLocation && jobLocation.location.id !== location.location.id) {
                addLocations.push({
                  locationId: jobLocation.location.id,
                  type: location.type,
                  order: location.order,
                  estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                });

                removeLocations.push(location.id);

                dialogInfo.locations.push({
                  currentLocation: location as CalendarEventLocation,
                  newLocation: {
                    address: jobLocation.location.addressLineOne,
                    locationId: jobLocation.location.id,
                    locationType: location.type,
                  }
                });
            }
          });

          if (addLocations.length || removeLocations.length) {
            edits.push({
              id: event.id,
              edit: {
                setLocations: {
                  addLocations,
                  removeLocations
                }
              }
            });
          }

          if (dialogInfo.locations.length) {
            confirmDialogInfo.push(dialogInfo);
          }
        });

        if (!edits.length) return of(JobToolActions.noOp());

        const hasLockedEvents = confirmDialogInfo.some(({ event }) => freyaHelper.lockDate > event.end);

        if (hasLockedEvents) {
          notify.warning(
            'Job location will be updated but cannot automatically update event locations as some events end before the lock date'
          );
          return of(JobToolActions.noOp());
        }

        const dialogRef = dialogService.open(UpdateEventLocationsDialogComponent, {
          header: 'Update Event Locations?',
          contentStyle: freyaHelper.getDialogContentStyle('1.5rem'),
          width: responsiveHelper.dialogWidth,
          closable: false,
          data: { eventLocations: confirmDialogInfo },
        });

        return dialogRef.onClose.pipe(
          switchMap(confirmed => confirmed
            ? of(JobToolActions.eventUpdateRequested({ edits }))
            : of(JobToolActions.noOp())
          )
        );
      }),
    );
  },
  { functional: true, dispatch: true }
);

export const GetInvoiceDetails = createEffect(
  (
    store = inject(Store),
    actions$ = inject(Actions),
    invoiceDetailsGQL = inject(InvoiceDetailsGQL)
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.jobLoaded),
      withLatestFrom(store.select(selectLatestInvoiceId)),
      filter(([_, latestInvoiceId]) => Boolean(latestInvoiceId)),
      switchMap(([_, latestInvoiceId]) => {
        return invoiceDetailsGQL.fetch({ invoiceId: latestInvoiceId }).pipe(
          map((res) =>{
            const artifact = res?.data?.invoices?.invoices[0]?.artifacts[0];
            return JobToolActions.getInvoiceDetailsSuccess({ artifact, documentType: latestInvoiceId });
          }),
          catchError((error) =>
            of(JobToolActions.getInvoiceDetailsError({ error }))
          )
        );
      }),
    );
  },
  { functional: true, dispatch: true }
);

export const updateJobState = createEffect(
  (
    actions$ = inject(Actions),
    updateJobGQL = inject(UpdateJobGQL),
    notify = inject(FreyaNotificationsService),
    detailsHelper = inject(DetailsHelperService),
  ) => {
    return actions$.pipe(
      ofType(JobToolActions.closeJob, JobToolActions.reopenJob),
      filter((action) => {
        // Do not execute if save form changes is set to true as this will be handled in a different effect
        if (action.type === JobToolActions.closeJob.type && action.saveFormChanges) {
          return false;
        }
        return true;
      }),
      exhaustMap((action) => {
        if (!action.jobId) {
          return of(JobToolActions.jobStateUpdatedError({
            error: new Error(`No jobId provided`),
          }));
        }

        const input: UpdateJobInput = {
          jobId: action.jobId,
        };

        let successText: string;
        let errorText: string;

        if (action.type === JobToolActions.closeJob.type) {
          input.closeJob = true;
          input.closedReason = action.closedReason;
          successText = 'Job closed';
          errorText = 'Error closing job';
        } else if (action.type === JobToolActions.reopenJob.type) {
          input.closeJob = false;
          successText = 'Job opened';
          errorText = 'Error opening job';
        }

        return updateJobGQL.mutate({ updateJobs: [input] },  {context: generateMutationContext()}).pipe(
          map((result) => {
            const {isSuccess, jobs: [updatedJob]} = result.data?.updateJobs;

            if (isSuccess) {
              notify.success(successText);
              return JobToolActions.jobStateUpdatedSuccess({
                closedAt: currentTimeSeconds(),
                state: input.closeJob ? 'closed' : 'open',
                closedReason: input.closedReason,
                updatedJob,
              });
            } else {
              const error = new Error(errorText);
              notify.error(errorText);
              return JobToolActions.jobStateUpdatedError({
                error,
              });
            }
          }),
          catchError((error) => {
            notify.apolloError(errorText, error);
            return of(
              JobToolActions.jobStateUpdatedError({
                error,
              }),
            );
          }),
        );
      }),
    );
  },
  { functional: true, dispatch: true },
);

export const openCloseJobSelectReasonDialog = createEffect((
  actions$ = inject(Actions),
  dialogService = inject(DialogService),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.closeJobButtonClicked),
      map(({ job, header }) => {
        header = header || 'Close Job';

        dialogService.open(CloseJobV2Component, {
          header,
          width: '20rem',
          data: {
            job,
          } as CloseJobDialogState,
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const deleteJob = createEffect((
  actions$ = inject(Actions),
  deleteJobGQL = inject(DeleteJobGQL),
  notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobToolActions.jobDeletedRequested),
      exhaustMap(({ jobId }) => deleteJobGQL.mutate({ jobIds: [ jobId ] }).pipe(
        map((res) => {

          const success = res.data?.deleteJobs;

          if (success) {
            notify.success('Job deleted successfully');
            return JobToolActions.jobDeletedSuccess({ jobId });
          }
          notify.error('Failed to delete job');
          return JobToolActions.jobDeletedError({ error: new Error('Failed to delete job') });
        }),
        catchError((error) => of(JobToolActions.jobDeletedError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true },
);

export const redirectToJobsPageOnDeleteJobSuccess = createEffect((
  actions$ = inject(Actions),
    router = inject(Router),
) => {

    const currentUrl$ = getCurrentUrl(router);

    return actions$.pipe(
      ofType(JobToolActions.jobDeletedSuccess),
      withLatestFrom(currentUrl$),
      filter(([ action, url ]) => isJobRoute(action.jobId, url)),
      tap(() => router.navigate([ '/jobs'])),
    );
  },
  { functional: true, dispatch: false },
);

function isJobRoute(jobId: string, url: string) {
  if (!url) { return false; }
  const [ _blank,  page, jobIdParam ] = url.split('/');
  if (page !== 'jobs') { return false; }
  return jobId === jobIdParam;
}

function getCurrentUrl(router: Router) {
  return router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(() => router.routerState.snapshot.url),
  );
}

export const copyJobCodeToClipboardAndOpenJobInRightPanel = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    localNotify = inject(FreyaNotificationsService),
    clipboard = inject(Clipboard),
    detailsHelper = inject(DetailsHelperService)
) => {
    return actions$.pipe(
        ofType(JobToolActions.jobCodeClicked),
        withLatestFrom(
            store.select(jobToolFeature.selectJob)
        ),
        map(([_, job]) => {

          const plainText = job.code;
          const htmlText = `<a href="/jobs/${job.id}/overview?zone=${job.zone.id}">${job.code}</a>`;

          if (navigator.clipboard && navigator.clipboard.write) {
            const clipboardItem = new ClipboardItem({
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
              'text/html': new Blob([htmlText], { type: 'text/html' }),
            });
            navigator.clipboard.write([clipboardItem]).then(
              () => localNotify.info('Job code and link copied to clipboard', job.code),
              (err) => console.error('Failed to copy job code:', err)
            );
          } else {
            // Fallback for older browsers
            clipboard.copy(plainText);
            console.warn('HTML copy not supported in this browser');
          }

          // Open job details in the right panel
          detailsHelper.open('job', job);
        })
      );
}, {functional: true, dispatch: false});
